import React, { useState, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { Row, Col, Card, Statistic, Radio, Alert, Space } from "antd";
import DatePicker from "@general/datePicker";
import moment from "moment-jalaali";

export const GET_ACCOUNTING_SUMMARY = gql`
  query GetAccountingSummary(
    $searchInput: FinancialChartSearchInput
    $chartTime: ChartTime!
  ) {
    getFunancialChart(searchInput: $searchInput, chartTime: $chartTime) {
      labels
      incomesValues
      expendituresValues
      profitsValues
    }
  }
`;

function AccountingSummary() {
  const [period, setPeriod] = useState("DAILY");
  const [selectedDate, setSelectedDate] = useState(() => moment());

  const { startIso, endIso, chartTime } = useMemo(() => {
    const m = selectedDate ? selectedDate.clone() : moment();
    let start;
    let end;
    let timeMode;

    if (period === "DAILY") {
      start = m.clone().startOf("day").toDate().toISOString();
      end = m.clone().endOf("day").toDate().toISOString();
      timeMode = "DAILY";
    } else if (period === "MONTHLY") {
      start = m.clone().startOf("jMonth").toDate().toISOString();
      end = m.clone().endOf("jMonth").toDate().toISOString();
      timeMode = "MONTHLY";
    } else {
      // YEARLY
      start = m.clone().startOf("jYear").toDate().toISOString();
      end = m.clone().endOf("jYear").toDate().toISOString();
      timeMode = "MONTHLY";
    }

    return {
      startIso: start,
      endIso: end,
      chartTime: timeMode,
    };
  }, [period, selectedDate]);

  const { data, loading, error } = useQuery(GET_ACCOUNTING_SUMMARY, {
    fetchPolicy: "no-cache",
    variables: {
      searchInput: {
        occurrenceAt: [startIso, endIso],
      },
      chartTime,
    },
  });

  const { totalIncome, totalExpenditure } = useMemo(() => {
    if (!data?.getFunancialChart) {
      return { totalIncome: 0, totalExpenditure: 0 };
    }

    const chart = data.getFunancialChart;
    if (period === "YEARLY") {
      const inc = (chart.incomesValues || []).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0
      );
      const exp = (chart.expendituresValues || []).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0
      );
      return { totalIncome: inc, totalExpenditure: exp };
    }

    return {
      totalIncome: Number(chart.incomesValues?.[0]) || 0,
      totalExpenditure: Number(chart.expendituresValues?.[0]) || 0,
    };
  }, [data, period]);

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  const pickerMode =
    period === "DAILY" ? "date" : period === "MONTHLY" ? "month" : "year";
  const pickerFormat =
    period === "DAILY"
      ? "jYYYY/jMM/jDD"
      : period === "MONTHLY"
      ? "jYYYY/jMM"
      : "jYYYY";

  return (
    <div style={{ padding: "8px 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "24px",
        }}
      >
        <Radio.Group
          value={period}
          onChange={handlePeriodChange}
          buttonStyle="solid"
        >
          <Radio.Button value="DAILY">روزانه</Radio.Button>
          <Radio.Button value="MONTHLY">ماهانه</Radio.Button>
          <Radio.Button value="YEARLY">سالانه</Radio.Button>
        </Radio.Group>

        <Space wrap>
          <span style={{ fontWeight: 500 }}>انتخاب بازه:</span>
          <DatePicker
            key={pickerMode}
            picker={pickerMode}
            value={selectedDate}
            onChange={(date) => {
              if (date) setSelectedDate(date);
            }}
            format={pickerFormat}
            allowClear={false}
          />
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="خطا در دریافت اطلاعات خلاصه حسابداری"
          description={error.message}
          style={{ marginBottom: 20 }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card
              size="small"
              bordered
              style={{
                borderRadius: 8,
                textAlign: "center",
                backgroundColor: "#f6ffed",
                borderColor: "#b7eb8f",
              }}
            >
              <Statistic
                title="مجموع درآمد"
                value={totalIncome}
                suffix="ریال"
                loading={loading}
                valueStyle={{ color: "#389e0d", fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              size="small"
              bordered
              style={{
                borderRadius: 8,
                textAlign: "center",
                backgroundColor: "#fff1f0",
                borderColor: "#ffa39e",
              }}
            >
              <Statistic
                title="مجموع هزینه"
                value={totalExpenditure}
                suffix="ریال"
                loading={loading}
                valueStyle={{ color: "#cf1322", fontWeight: 700 }}
              />
            </Card>
          </Col>
        </Row>
      )}

    </div>
  );
}

export default AccountingSummary;
